import React, { useRef, useState } from "react";
import { CustomIcon } from "./CustomIcon";
import { compressImageDataUrl } from "./PhotoUploader";

// ============================================================
// CoverImage — Phase 5A shared cover-image primitive.
// ============================================================
// Renders a 16:9 banner with:
//   • Uploaded base64 image (JPG / PNG / WEBP, ≤ 5 MB source,
//     compressed to ≤ 400 KB @ max-dim 1200 for banner quality).
//   • Gradient overlay (linear-gradient to top, 55 % black → 60 %
//     transparent) so bottom-left text stays legible.
//   • Placeholder tile using var(--kc-accent-grad) at 40 % opacity
//     + centred 32 px CustomIcon when no image is set.
//   • Optional title (rendered in white Cinzel) at the bottom-left.
//
// Two modes via the `editable` prop:
//   editable=false → read-only display (list cards, detail hero).
//   editable=true  → clickable overlay with an "Upload Cover Image"
//                    button and a "Remove" affordance when set.
//
// Rounded top corners only (radius-top). Bottom is square so the
// card body below connects flush.
// ============================================================

const ACCEPT_TYPES = ["image/jpeg", "image/png", "image/webp"];
const HARD_LIMIT_BYTES = 5 * 1024 * 1024; // 5 MB source cap per spec

export function CoverImage({
  value,
  onChange,
  title,
  subtitle,
  iconName = "camera-upload",
  editable = false,
  overlay = 55,                 // 0–100 — used by textColor="auto"
  textColor = "white",          // 'white' | 'black' | 'auto'
  textEffect = "shadow",        // 'none' | 'shadow' | 'stroke' | 'both'
  className = "",
  style = {},
  "data-testid": testId,
}) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const openPicker = () => {
    setErr("");
    if (fileRef.current) fileRef.current.click();
  };

  const handleFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (!ACCEPT_TYPES.includes(f.type)) {
      setErr("Only JPG, PNG, or WEBP images are supported.");
      return;
    }
    if (f.size > HARD_LIMIT_BYTES) {
      setErr("Image is larger than 5 MB — please choose a smaller file.");
      return;
    }
    setBusy(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((res, rej) => {
        reader.onload = () => res(reader.result);
        reader.onerror = () => rej(new Error("Failed to read file"));
        reader.readAsDataURL(f);
      });
      // Compress: allow wider dimension for banners + slightly higher
      // byte cap so the gradient still looks premium at 16:9.
      const compressed = await compressImageDataUrl(dataUrl, {
        maxDimension: 1200,
        maxBytes: 400 * 1024,
      });
      onChange && onChange(compressed);
    } catch (ex) {
      setErr(String(ex?.message || ex) || "Failed to process image.");
    } finally {
      setBusy(false);
    }
  };

  const clear = () => { onChange && onChange(""); };
  const hasImage = typeof value === "string" && value.length > 0;

  return (
    <div
      className={`kc-cover ${className}`}
      data-testid={testId || "cover-image"}
      style={{
        position: "relative",
        width: "100%",
        aspectRatio: "16 / 9",
        borderTopLeftRadius: "var(--kc-radius)",
        borderTopRightRadius: "var(--kc-radius)",
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        overflow: "hidden",
        background: hasImage
          ? undefined
          : "linear-gradient(rgba(255,255,255,0.60), rgba(255,255,255,0.60)), var(--kc-accent-grad)",
        ...style,
      }}
    >
      {hasImage ? (
        <img
          src={value}
          alt={title || "Cover"}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", position: "relative", zIndex: 1 }}
        />
      ) : (
        <div style={{
          position: "absolute", inset: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "var(--kc-accent)",
          zIndex: 1,
        }}>
          <CustomIcon name={iconName} size={32} />
        </div>
      )}
      {/* Legibility gradient: intensity driven by `overlay` prop (0–100). */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", inset: 0,
          background: `linear-gradient(to top, rgba(0,0,0,${Math.max(0, Math.min(100, overlay)) / 100}) 0%, rgba(0,0,0,0) 60%)`,
          pointerEvents: "none",
          zIndex: 2,
        }}
      />

      {(title || subtitle) && (() => {
        // Resolve the effective foreground colour + effect.
        const effColor = textColor === "auto"
          ? (overlay >= 40 ? "#ffffff" : "#111111")
          : (textColor === "black" ? "#111111" : "#ffffff");
        const isWhite = effColor === "#ffffff";
        const shadowCss = "0 2px 4px rgba(0,0,0,0.8)";
        const strokeCss = isWhite
          ? "1px rgba(0,0,0,0.6)"
          : "1px rgba(255,255,255,0.6)";
        const useShadow = textEffect === "shadow" || textEffect === "both";
        const useStroke = textEffect === "stroke" || textEffect === "both";
        const textShadow = useShadow ? shadowCss : "none";
        const webkitStroke = useStroke ? strokeCss : "0";
        return (
          <div
            data-testid={`${testId || "cover-image"}-text`}
            style={{
              position: "absolute", left: "var(--space-md)", right: "var(--space-md)",
              bottom: "var(--space-sm)",
              color: effColor,
              zIndex: 4,
            }}
          >
            {title && (
              <div style={{
                fontFamily: "'Cinzel', serif",
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: "0.02em",
                lineHeight: 1.15,
                textShadow,
                WebkitTextStroke: webkitStroke,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                color: effColor,
              }}>{title}</div>
            )}
            {subtitle && (
              <div style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 11, fontWeight: 500,
                marginTop: 2,
                textShadow,
                WebkitTextStroke: webkitStroke,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                color: effColor,
              }}>{subtitle}</div>
            )}
          </div>
        );
      })()}

      {editable && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFile}
            style={{ display: "none" }}
            data-testid={`${testId || "cover-image"}-input`}
          />
          <div style={{
            position: "absolute", right: "var(--space-sm)", top: "var(--space-sm)",
            display: "flex", gap: "var(--space-xs)",
            zIndex: 4,
          }}>
            <button
              type="button"
              onClick={openPicker}
              disabled={busy}
              data-testid={`${testId || "cover-image"}-upload`}
              style={{
                background: "rgba(0,0,0,0.55)",
                color: "var(--kc-text)",
                border: "1px solid rgba(255,255,255,0.35)",
                borderRadius: "var(--kc-radius-xs)",
                padding: "6px 12px",
                fontFamily: "'Inter', sans-serif",
                fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
                textTransform: "uppercase",
                cursor: busy ? "wait" : "pointer",
                display: "inline-flex", alignItems: "center", gap: "var(--space-xs)",
                backdropFilter: "blur(6px)",
                WebkitBackdropFilter: "blur(6px)",
              }}
            >
              {busy ? "Uploading…" : "Upload Cover Image"}
            </button>
            {hasImage && !busy && (
              <button
                type="button"
                onClick={clear}
                data-testid={`${testId || "cover-image"}-remove`}
                aria-label="Remove cover image"
                style={{
                  background: "rgba(239,68,68,0.85)",
                  color: "var(--kc-text)",
                  border: "none",
                  borderRadius: "var(--kc-radius-xs)",
                  padding: "6px 10px",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 11, fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            )}
          </div>
          {err && (
            <div style={{
              position: "absolute", left: "var(--space-sm)", top: "var(--space-sm)",
              background: "rgba(239,68,68,0.90)", color: "var(--kc-text)",
              padding: "4px 8px", borderRadius: "var(--kc-radius-xs)",
              fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 700,
              maxWidth: "60%",
            }}>{err}</div>
          )}
        </>
      )}
    </div>
  );
}

export default CoverImage;
